import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames/bind';
import { EmojiIcon } from '../EmojiIcon';
import { getUserResponse } from '../../shared/answer-helpers';
import { BOOK_ARCHETYPES, ARCHETYPE_CONTENT, getBookArchetype, getThicknessIndex, getHeatIndex } from '../../shared/book-archetype-helpers';
import { getBookCover } from '../../shared/book-covers';
import styles from './ProfileSlideV5.css';

const cx = classnames.bind(styles);

const THICKNESS_LEVELS = [
    { title: 'Pocket Edition', comment: 'Short, sharp, no filler. Every page is a highlight.' },
    { title: 'One Thick Novel', comment: 'The kind of book people finish at 2 a.m. and then text you about.' },
    { title: 'A Trilogy', comment: 'Three books, three women, all of them you. Honestly, iconic.' },
    { title: 'A Five-Volume Saga', comment: 'Five volumes and a family tree. You really outdid yourself.' }
];

const HEAT_LEVELS = [
    { title: 'Heartfelt', comment: 'Warm, honest, read-it-twice. The kind of book people keep.' },
    { title: 'Slow Burn', comment: 'It starts gently, then nobody can put it down.' },
    { title: 'Spicy', comment: 'A few pages your mother will skip. You did go all out.' },
    { title: 'Hide the Cover', comment: 'Read in public at your own risk. Iconic behaviour.' }
];

function ProfileSlideV5({ results, spriteVersion }) {
    const { userResponses } = results;
    const name = getUserResponse(userResponses, 9114)?.text;
    const titleResponse = getUserResponse(userResponses, 9111);
    const dedication = getUserResponse(userResponses, 9112)?.text;
    const archetype = getBookArchetype(userResponses);
    const { heroine, genre, comment } = ARCHETYPE_CONTENT[archetype];
    const thicknessIndex = getThicknessIndex(userResponses);
    const heatIndex = getHeatIndex(userResponses);

    const renderScale = (icon, activeCount) => [0, 1, 2, 3].map(index => (
        <EmojiIcon
            key={index}
            name={icon}
            version={spriteVersion}
            className={cx({ inactive: index >= activeCount })}
        />
    ));

    const renderPart = (label, scale, title, text) => (
        <div className={styles.part}>
            <div className={styles.partRow}>
                <span className={styles.partLabel}>{label}</span>
                <span className={styles.scale} aria-hidden='true'>{scale}</span>
            </div>
            <h3 className={styles.partTitle}>{title}</h3>
            <p className={styles.partText}>{text}</p>
        </div>
    );

    return (
        <section className={styles.root}>
            <h1 className={styles.heading}>
                {name ? <><span className={styles.accent}>{name},</span> your book is ready!</> : 'Your book is ready!'}
            </h1>
            <article className={styles.card}>
                <div className={styles.coverWrap}>
                    <img src={getBookCover(userResponses)} alt='' className={styles.cover} />
                </div>
                <div className={styles.coverInfo}>
                    <p className={styles.heroine}>{heroine}</p>
                    <p className={styles.bookTitle}>{titleResponse?.text}</p>
                    {Boolean(name) && <p className={styles.author}>by {name}</p>}
                </div>
                {renderPart(
                    'The Thickness',
                    renderScale('open-book', thicknessIndex + 1),
                    THICKNESS_LEVELS[thicknessIndex].title,
                    THICKNESS_LEVELS[thicknessIndex].comment
                )}
                {renderPart(
                    'The Genre',
                    BOOK_ARCHETYPES.map(key => (
                        <EmojiIcon
                            key={key}
                            name={ARCHETYPE_CONTENT[key].icon}
                            version={spriteVersion}
                            className={cx({ inactive: key !== archetype })}
                        />
                    )),
                    genre,
                    comment
                )}
                {renderPart(
                    'The Heat',
                    renderScale('hot-pepper', heatIndex + 1),
                    HEAT_LEVELS[heatIndex].title,
                    HEAT_LEVELS[heatIndex].comment
                )}
                {Boolean(dedication) && <p className={styles.dedication}>{dedication}</p>}
            </article>
            <div className={styles.persuade}>
                <p className={styles.persuadeTitle}>This book deserves to exist.</p>
                <p className={styles.persuadeText}>Look at that. It’s already a book people would want to read.</p>
                <p className={styles.persuadeText}>You have everything it needs: the memories, the stories people beg you to tell again, and that voice. Don’t let it live only in a quiz. We’ll help you write it, one story at a time, just by talking.</p>
            </div>
        </section>
    );
}

ProfileSlideV5.propTypes = {
    results: PropTypes.shape({ userResponses: PropTypes.array }).isRequired,
    spriteVersion: PropTypes.string.isRequired
};

export {
    ProfileSlideV5
};
